import { type ThirdPartyProductFilter } from '@atlassian/search-client/query-generator';

import { type ProductKeys3P } from '../3p-product-configs';
import type { FilterId } from '../filters';
import type { TypeFilterConfig3P } from '../filters/universal-type';
import { type RovoLogo } from '../icons';

import { ConnectorSources } from './connector-sources';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { ConnectorSources } from './connector-sources';

/**
 * Type for Platform Integration ARI.
 * The API always returns integrationId in this format.
 */
export type PlatformIntegrationAri = `ari:cloud:platform::integration/${string}`;

/**
 * Configuration for a third-party connector.
 */
export interface ThirdPartyConfig {
	/**
	 * The displayed name of the connector.
	 */
	displayName: React.ReactNode;

	/**
	 * The unique identifier for the connector.
	 */
	id: string;

	/**
	 * The resource type for the 3P config endpoint.
	 * Optional: Some connectors like Loom do not have this.
	 */
	resourceType?: string;

	/**
	 * The icon for the connector.
	 */
	icon: RovoLogo;

	/**
	 * The borderless icon for the connector, used for scenarios where the icon is inside a container with a stroke.
	 */
	iconBorderless?: RovoLogo;

	/**
	 * The search result icon for the connector.
	 */
	searchResultIcon?: RovoLogo;

	/**
	 * The integration ARI (Atlassian Resource Identifier) for the connector.
	 */
	integrationAri: PlatformIntegrationAri;

	/**
	 * The list of types keys that can be used to search for this 3P.
	 *
	 * This powers both the type filter AND the entities that are requested from Aggregator during a search
	 *
	 * For how values map to nouns and subtypes please see ../nouns-and-types
	 */
	typeFilterConfig?: TypeFilterConfig3P;

	/**
	 * The filters available for the connector. The Universal Type Filter is included by default, so it is not necessary to include it here.
	 */
	filters?: FilterId[];

	/**
	 * The OAuth outbound URL for the connector. It is populated at runtime.
	 */
	outboundAuthUrl?: string;

	/**
	 * Indicates whether a product requires Oauth.
	 */
	requiresOauth: boolean;

	/**
	 * Indicates whether the user is authenticated via OAuth. It is populated at runtime.
	 */
	userNeedsOAuth?: boolean;

	/**
	 * Indicates whether the connector is enabled
	 */
	isEnabled: () => boolean;

	/**
	 * Indicates whether the connector is federated
	 * and therefore doesn't need a thirdPartyProduct filter
	 * in the QGL query
	 */
	isFederated?: boolean;

	/**
	 * Some products might want to display the customer-specific
	 * workspace name instead of the product name
	 * e.g. "Bulldog Wiki" instead of "Confluence"
	 */
	displayWorkspaceNameAsProductName?: boolean;

	/**
	 * Some products might want to display multiple sources
	 * such as webcrawler wanting to show different source sites
	 * e.g. "methoda.com" & "developer.atlassian.com" instead of "Web Page"
	 */
	fanOutConnectorSources?: boolean;

	/**
	 * Filter results by the entity's container type
	 */
	containerTypes?: string[];

	/**
	 * Filter results by the integrationId's for Jira and JSM data
	 */
	integrationId?: string;

	/**
	 * Get filters for a specific datasource
	 * Returns an object mapping filter names to their values
	 * For example, web crawler maps workspaceUrl to additionalTexts
	 */
	getSelectedDatasourceFilters?: (
		datasource: ThirdPartyConfigsWithAPIResponse,
	) => Partial<ThirdPartyProductFilter>;
}

export const SMARTLINKS_RESOURCE_TYPE_PREFIX = 'smartlinks-connector';

export const FEDERATED_CONNECTOR_SOURCE: 'federated' = ConnectorSources.Federated;
export const LITE_CONNECTOR_SOURCE: 'lite-connector' = ConnectorSources.Lite;
export const SMARTLINKS_CONNECTOR_SOURCE: 'smartlinks' = ConnectorSources.Smartlinks;
export const FULL_CONNECTOR_SOURCE: 'full-connector' = ConnectorSources.Full;
export const BACKFILL_CONNECTOR_SOURCE: 'backfill' = ConnectorSources.Backfill;

export type ConnectorSource = (typeof ConnectorSources)[keyof typeof ConnectorSources];

export const isConnectorSource: (value: string | null | undefined) => value is ConnectorSource = (
	value: string | null | undefined,
): value is ConnectorSource => {
	return Object.values(ConnectorSources).includes(value as ConnectorSource);
};

/**
 * Prefix used for all dynamically loaded Forge connector product keys.
 * All Forge connectors will have an id in the format: forge-connector-{uniqueId}-{connectorName}
 */
export const FORGE_CONNECTOR_PREFIX = 'forge-connector-';

/**
 * Type alias for Forge connector product keys.
 *
 * Note: Using `string` instead of template literal type `forge-connector-${string}` to avoid
 * TypeScript issues with Object.fromEntries() and dynamic object construction. Runtime validation
 * is still enforced via isForgeConnectorProductKey() type guard.
 */
export type ForgeConnectorProductKey = string;

/**
 * Type guard to check if a product key is a Forge connector.
 * All dynamic Forge connectors will have an id starting with 'forge-connector-'.
 */
export const isForgeConnectorProductKey: (type: string) => type is ForgeConnectorProductKey = (
	type: string,
): type is ForgeConnectorProductKey => {
	return type.startsWith(FORGE_CONNECTOR_PREFIX);
};

/**
 * Runtime registry for Forge connector icons.
 * Similar to ThirdPartyConfigs, but populated dynamically from API responses.
 */
const forgeConnectorIconsRegistry = new Map<ForgeConnectorProductKey, RovoLogo>();

/**
 * Register an icon for a Forge connector product key.
 * Called when processing Forge connectors from the API.
 */
export const registerForgeConnectorIcon: (
	productKey: ForgeConnectorProductKey,
	icon: RovoLogo,
) => void = (productKey: ForgeConnectorProductKey, icon: RovoLogo): void => {
	forgeConnectorIconsRegistry.set(productKey, icon);
};

/**
 * Get the icon for a Forge connector product key.
 * Returns undefined if the icon hasn't been registered yet.
 */
export const getForgeConnectorIcon: (
	productKey: ForgeConnectorProductKey,
) => RovoLogo | undefined = (productKey: ForgeConnectorProductKey): RovoLogo | undefined => {
	return forgeConnectorIconsRegistry.get(productKey);
};

/**
 * Clear all registered Forge connector icons.
 * Useful for testing or when switching users/clouds.
 */
export const clearForgeConnectorIcons: () => void = (): void => {
	forgeConnectorIconsRegistry.clear();
};

// certain properties from ThirdPartyConfig are redefined as they are guaranteed
export type ThirdPartyConfigsWithAPIResponse = ThirdPartyConfig & {
	providerId: string;
	outboundAuthUrl: string;
	userNeedsOAuth: boolean;
	workspaceName?: string;
	connectionId?: string;
	connectorSources?: ConnectorSource[];
	workspaceUrl?: string;
	datasourceId?: string;
	datasources?: ThirdPartyConfigsWithAPIResponse[];
	impersonationEnabled?: boolean;
	connectorCreatedAt?: string;
};

export type ThirdPartyConfigsBootstrap = Partial<
	Record<ProductKeys3P | ForgeConnectorProductKey, ThirdPartyConfigsWithAPIResponse>
>;
